import {
  Cryptocurrency,
  Currency,
  SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import {
  cryptocurrencyLabels,
  cryptocurrencySymbols,
  placeholderAddresses,
} from '#src/lib/cryptocurrency.ts';
import {
  RoundingMode,
  SOL_DECIMAL_PLACES,
  fromLamport,
} from '#src/lib/lamport.ts';
import { ETH_DECIMAL_PLACES, fromWei } from '#src/lib/wei.ts';

import { useAmountWithConversion } from './useAmountWithConversion.ts';
import { useMonetaryAmount } from './useMonetaryAmount.ts';

export const useCryptoBalance = (hideSymbol?: boolean) => {
  const { currentUser, currency, cryptocurrency } = useCurrentUserContext();
  const { toMonetaryAmount } = useMonetaryAmount();

  const availableBalances = currentUser?.availableBalances;

  const {
    availableBalance,
    monetaryBalance,
    roundedCryptoValue,
    supportedCurrency,
  } = (() => {
    switch (cryptocurrency) {
      case Cryptocurrency.LAMPORT: {
        const mb = toMonetaryAmount(availableBalances?.lamport);
        return {
          availableBalance: mb.lamport,
          monetaryBalance: mb,
          roundedCryptoValue: fromLamport(
            mb.lamport,
            SOL_DECIMAL_PLACES,
            RoundingMode.ROUND_DOWN
          ),
          supportedCurrency: SupportedCurrency.LAMPORT,
        };
      }
      case Cryptocurrency.WEI:
      default: {
        const mb = toMonetaryAmount(availableBalances?.wei);
        return {
          availableBalance: mb.wei,
          monetaryBalance: mb,
          roundedCryptoValue: fromWei(
            mb.wei,
            ETH_DECIMAL_PLACES,
            RoundingMode.ROUND_DOWN
          ),
          supportedCurrency: SupportedCurrency.WEI,
        };
      }
    }
  })();

  const { main, exponent, cryptoAmount } = useAmountWithConversion({
    monetaryAmount: monetaryBalance,
    hideSymbol,
    useGrouping: false,
  });
  const hideBalance = currentUser?.userSettings?.hideBalance;

  return {
    availableBalance,
    monetaryBalance,
    roundedCryptoValue,
    cryptocurrency,
    supportedCurrency,
    hideBalance,
    main,
    exponent,
    balanceInFiat: currency === Currency.ETH ? exponent : main,
    balanceInCrypto: currency === Currency.ETH ? main : exponent,
    cryptoAmount,
    cryptocurrencySymbol: cryptocurrencySymbols[cryptocurrency],
    cryptocurrencyLabel: cryptocurrencyLabels[cryptocurrency],
    placeholderAddress: placeholderAddresses[cryptocurrency],
  };
};
